import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, MapPin, CheckCircle, Wrench, Factory, Shield, Zap, Menu, X, ChevronRight, ArrowRight, Clock, AlertTriangle, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaPosObraIndustrial = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = (contexto = 'industrial') => {
    const mensagens = {
      industrial: 'Olá! Preciso de limpeza pós-obra industrial. Gostaria de receber um orçamento.',
      orcamento: 'Olá! Gostaria de solicitar avaliação e orçamento de limpeza pós-obra para área industrial.',
      info: 'Olá! Gostaria de saber mais sobre limpeza pós-obra industrial.'
    };
    
    const message = encodeURIComponent(mensagens[contexto] || mensagens.industrial);
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  const setoresAtendidos = [
    {
      setor: 'Indústrias de Manufatura',
      descricao: 'Limpeza pós-obra em plantas industriais, áreas de produção e montagem após expansões ou reformas.',
      casos: ['Novos pavilhões', 'Ampliação de linhas', 'Retrofit de áreas', 'Adequação de layout']
    },
    {
      setor: 'Indústrias Químicas e Farmacêuticas',
      descricao: 'Limpeza técnica respeitando normas de higiene industrial e preparação para validação de ambientes.',
      casos: ['Salas limpas', 'Áreas classificadas', 'Laboratórios', 'Áreas de processo']
    },
    {
      setor: 'Indústrias de Alimentos e Bebidas',
      descricao: 'Limpeza pós-obra preparatória para certificação sanitária e início de produção.',
      casos: ['Plantas novas', 'Reformas sanitárias', 'Áreas de envase', 'Câmaras frias']
    },
    {
      setor: 'Metalúrgicas e Mecânicas',
      descricao: 'Remoção de resíduos pesados, poeira industrial e preparo de piso para operação de máquinas.',
      casos: ['Oficinas industriais', 'Áreas de usinagem', 'Fundição', 'Caldeiraria']
    }
  ];

  const pisoIndustrial = [
    {
      tipo: 'Concreto Industrial',
      caracteristicas: 'Piso mais comum em indústrias. Resistente mas poroso, absorve resíduos.',
      cuidados: 'Requer equipamentos com pressão controlada e produtos adequados para não danificar'
    },
    {
      tipo: 'Epóxi',
      caracteristicas: 'Revestimento liso e impermeável. Comum em áreas limpas e farmacêuticas.',
      cuidados: 'Não pode usar escovas muito abrasivas. Produtos neutros ou específicos para epóxi'
    },
    {
      tipo: 'Poliuretano',
      caracteristicas: 'Alta resistência química e mecânica. Usado em indústrias químicas.',
      cuidados: 'Limpeza técnica com produtos compatíveis. Evitar solventes agressivos'
    },
    {
      tipo: 'Concreto Polido',
      caracteristicas: 'Superfície lisa e densa. Estética industrial moderna.',
      cuidados: 'Requer pads específicos e produtos de pH controlado para manter brilho'
    }
  ];

  const faqIndustrial = [
    {
      question: 'Qual a diferença entre limpeza pós-obra industrial e comercial?',
      answer: 'A limpeza industrial exige conhecimento técnico sobre tipos de piso industrial (epóxi, poliuretano, concreto polido), resíduos industriais específicos, normas de segurança e muitas vezes preparação para validação de ambientes. Também demanda equipamentos de maior porte para grandes áreas.'
    },
    {
      question: 'Vocês trabalham com piso de epóxi?',
      answer: 'Sim. Temos experiência em limpeza pós-obra de pisos industriais com revestimento epóxi. Utilizamos equipamentos e produtos adequados que não danificam o acabamento, respeitando as características do revestimento.'
    },
    {
      question: 'Fazem limpeza em área com risco químico ou biológico?',
      answer: 'Avaliamos caso a caso. Para áreas classificadas ou com risco químico/biológico, nossa equipe segue os protocolos de segurança definidos pela empresa contratante. Em alguns casos, a limpeza é realizada sob supervisão da equipe de segurança do trabalho do cliente.'
    },
    {
      question: 'Quanto custa limpeza pós-obra industrial?',
      answer: 'O investimento varia conforme metragem, tipo de piso industrial, nível de sujeira, presença de resíduos químicos, acesso, e necessidade de trabalho em horário especial. Solicite orçamento informando tipo de indústria, metragem aproximada e cidade.'
    },
    {
      question: 'Vocês emitem documentação técnica?',
      answer: 'Sim. Fornecemos relatório fotográfico antes/depois, certificado de execução de serviço, e quando necessário, documentação técnica dos produtos utilizados (FISPQ). Essa documentação é importante para validação de ambientes industriais.'
    },
    {
      question: 'Atendem em horário noturno ou finais de semana?',
      answer: 'Sim. Sabemos que muitas indústrias operam continuamente. Podemos executar a limpeza pós-obra em horários alternativos (noite, madrugada, finais de semana) para não interferir na produção ou na logística da planta.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Component */}
      <SEO 
        title={seoData['/limpeza-pos-obra-industrial'].title}
        description={seoData['/limpeza-pos-obra-industrial'].description}
        canonical={seoData['/limpeza-pos-obra-industrial'].canonical}
      />

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="text-green-400 font-semibold">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Locação</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 text-white py-24 md:py-32 overflow-hidden"
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              LIMPEZA TÉCNICA INDUSTRIAL
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Limpeza Pós-Obra Industrial em BH e Região
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-6 leading-relaxed font-semibold">
              Limpeza especializada para indústrias após obras, reformas e adequações de plantas industriais
            </p>

            <p className="text-lg text-gray-300 mb-8 max-w-3xl">
              Experiência com pisos industriais (epóxi, poliuretano, concreto polido), resíduos pesados e preparação de ambientes industriais para início de produção ou validação técnica.
            </p>

            <p className="text-sm text-blue-200 mb-10 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Belo Horizonte, Contagem, Betim e Região Metropolitana
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => handleWhatsAppClick('orcamento')}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento Industrial
              </Button>
              
              <Button 
                onClick={() => navigate('/limpeza-pos-obra')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg"
              >
                Ver Todos os Serviços
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Por Que Limpeza Industrial é Diferente */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Por Que Limpeza Industrial Exige Especialização?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Ambientes industriais apresentam desafios técnicos específicos que exigem conhecimento especializado, equipamentos adequados e produtos compatíveis com cada tipo de piso.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-100 hover:border-blue-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-blue-100 text-blue-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Shield className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Pisos Especiais</h3>
                  <p className="text-gray-600 text-sm">
                    Epóxi, poliuretano, concreto polido requerem produtos e técnicas específicas
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-blue-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-blue-100 text-blue-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <AlertTriangle className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Normas Técnicas</h3>
                  <p className="text-gray-600 text-sm">
                    Preparação para validação de ambientes, certificação sanitária e normas de segurança
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-blue-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-blue-100 text-blue-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Wrench className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Resíduos Pesados</h3>
                  <p className="text-gray-600 text-sm">
                    Cimento, argamassa, poeira industrial, resíduos químicos e contaminação cruzada
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Setores Industriais Atendidos */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Setores Industriais que Atendemos
              </h2>
              <p className="text-lg text-gray-600">
                Experiência em diferentes segmentos industriais
              </p>
            </div>

            <div className="space-y-6">
              {setoresAtendidos.map((setor, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="bg-green-600 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{setor.setor}</h3>
                        <p className="text-gray-600 mb-4">{setor.descricao}</p>
                        <div className="flex flex-wrap gap-2">
                          {setor.casos.map((caso, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                              {caso}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de Piso Industrial */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Experiência com Pisos Industriais
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Cada tipo de piso industrial exige equipamentos, produtos e técnicas específicas. Conhecemos as características de cada um.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {pisoIndustrial.map((piso, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-blue-500 transition-all">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{piso.tipo}</h3>
                    <p className="text-gray-600 mb-3 text-sm">
                      <strong>Características:</strong> {piso.caracteristicas}
                    </p>
                    <p className="text-blue-700 text-sm">
                      <strong>Cuidados:</strong> {piso.cuidados}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-8 bg-green-50 border-2 border-green-200">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Não sabe qual tipo de piso você tem?
                </h3>
                <p className="text-gray-700 mb-6">
                  Nossa equipe técnica avalia o tipo de piso no local e define o melhor método de limpeza
                </p>
                <Button 
                  onClick={() => handleWhatsAppClick('info')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Falar com Especialista
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Central */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <Factory className="w-16 h-16 mx-auto mb-6 text-blue-200" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Sua Indústria Precisa de Limpeza Pós-Obra?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Informe tipo de indústria, metragem e cidade para receber orçamento técnico personalizado
          </p>
          <Button 
            onClick={() => handleWhatsAppClick('orcamento')}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-10 py-6 text-lg font-bold"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Solicitar Orçamento Agora
          </Button>
        </div>
      </section>

      {/* Conteúdos Relacionados */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Conteúdos Relacionados
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Limpeza Pós-Obra Geral
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Conheça todos os nossos serviços de limpeza pós-obra
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ver serviços <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/limpeza-piso-pos-obra')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Guia: Limpeza de Piso Pós-Obra
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Tudo sobre limpeza por tipo de piso e equipamentos
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ver guia <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/locacao-lavadora-piso')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Locação de Equipamentos
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Prefere alugar? Confira nossas opções de locação
                  </p>
                  <span className="text-blue-600 font-semibold text-sm flex items-center gap-2">
                    Ver locação <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Seção de Serviços Relacionados - Linkagem Interna */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 md:p-12 border-2 border-slate-200">
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Conheça também nossos serviços
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Além da limpeza pós-obra industrial, a WellTec oferece soluções de limpeza mecanizada para diferentes tipos de ambientes e também locação de equipamentos para quem prefere realizar a limpeza com equipe própria.
                </p>
                <Link 
                  to="/limpeza-pos-obra"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg"
                >
                  Conheça nosso serviço de limpeza pós-obra para diferentes tipos de ambientes
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Links Relacionados */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Outros serviços WellTec:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link 
                  to="/limpeza-pos-obra-galpoes"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <Factory className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Limpeza de Galpões</p>
                    <p className="text-sm text-gray-600">Grandes áreas comerciais</p>
                  </div>
                </Link>
                
                <Link 
                  to="/locacao-lavadora-piso-industria"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <CheckCircle className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Locação para Indústrias</p>
                    <p className="text-sm text-gray-600">Equipamentos para sua equipe</p>
                  </div>
                </Link>

                <Link 
                  to="/locacao-lavadora-piso-belo-horizonte"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <MapPin className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Locação em BH</p>
                    <p className="text-sm text-gray-600">Atendimento na capital</p>
                  </div>
                </Link>

                <Link 
                  to="/servicos"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <Wrench className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Manutenção</p>
                    <p className="text-sm text-gray-600">Assistência técnica especializada</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Perguntas Frequentes sobre Limpeza Industrial
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqIndustrial.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-slate-50 border-2 border-gray-100 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                alt="WellTec" 
                className="h-16 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Limpeza pós-obra industrial especializada em BH.
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link></li>
                <li><Link to="/limpeza-pos-obra-industrial" className="hover:text-green-400 transition-colors">Limpeza Industrial</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400 transition-colors">Locação</Link></li>
                <li><Link to="/blog" className="hover:text-green-400 transition-colors">Blog</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-green-400 transition-colors">Início</Link></li>
                <li><Link to="/servicos" className="hover:text-green-400 transition-colors">Serviços</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>(31) 97182-3249</li>
                <li>BH e Região Metropolitana</li>
                <li>
                  <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">
                    @welltecbrasil
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 WellTec - Limpeza Pós-Obra Industrial. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LimpezaPosObraIndustrial;
